import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnDestroy,
} from '@angular/core';
import { IDocument } from '@pf/shared-common';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { DOCUMENT_IMAGE_GALLERY } from './document-image-gallery.tokens';

@Component({
    selector: 'pf-document-file-thumb',
    templateUrl: './pf-document-file-thumb.component.html',
    styleUrls: ['./pf-document-file-thumb.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PfDocumentFileThumbComponent implements OnDestroy {
    private readonly gallery = inject(DOCUMENT_IMAGE_GALLERY);
    private readonly documentSubject = new BehaviorSubject<IDocument | null>(
        null
    );
    private readonly parentIdSubject = new BehaviorSubject<string | undefined>(
        undefined
    );
    private readonly fallbackUrlSubject = new BehaviorSubject<
        string | null | undefined
    >(undefined);

    private currentObjectUrl: string | null = null;

    @Input() alt = '';

    @Input()
    set document(value: IDocument | null | undefined) {
        this.documentSubject.next(value ?? null);
    }

    @Input()
    set parentId(value: string | undefined) {
        this.parentIdSubject.next(value);
    }

    @Input()
    set fallbackUrl(value: string | null | undefined) {
        this.fallbackUrlSubject.next(value);
    }

    private readonly inputs$ = combineLatest([
        this.documentSubject,
        this.parentIdSubject,
        this.fallbackUrlSubject,
    ]).pipe(
        distinctUntilChanged(
            (a, b) =>
                a[0]?.id === b[0]?.id &&
                a[0]?.fileId === b[0]?.fileId &&
                a[1] === b[1] &&
                a[2] === b[2]
        )
    );

    readonly imageUrl$ = this.inputs$.pipe(
        switchMap(([doc, parentId, fallback]) => {
            if (this.currentObjectUrl) {
                URL.revokeObjectURL(this.currentObjectUrl);
                this.currentObjectUrl = null;
            }
            if (!doc?.fileId || !doc.id || !parentId?.trim()) {
                return of<string | null>(fallback ?? null);
            }
            return this.gallery.previewUrl$(doc, parentId).pipe(
                map(url => {
                    this.currentObjectUrl = url;
                    return url;
                }),
                catchError(() => of<string | null>(fallback ?? null))
            );
        })
    );

    ngOnDestroy(): void {
        if (this.currentObjectUrl) {
            URL.revokeObjectURL(this.currentObjectUrl);
            this.currentObjectUrl = null;
        }
    }
}
